import {
  JsonBasicItem,
  JsonGroup,
  JsonRule,
  JsonTree,
  RuleProperties,
} from '@react-awesome-query-builder/mui';

import { SearchFilter } from '@/types/searchFilterTypes';
import {
  SearchTreeInternalNode,
  SearchTreeLeafNode,
  SearchTreeNode,
} from '@/types/searchTreeTypes';
import { MapQbRulePropertiesToSearchFilterOptions } from './qbRulePropertiesMapping';

type RulePropsToSearchFilterMapper = (
  props: RuleProperties,
  options: MapQbRulePropertiesToSearchFilterOptions
) => SearchFilter;

/**
 * Converts the 'JsonTree' representation of a query (the output of
 * QbUtils.getTree(), not the internal immutable OrderedMap) and converts it to
 * the (the root SearchTreeNode of) a search tree suitable for the list manager.
 *
 * No user permissions are stored in the search tree, with that being handled
 * by the backend.
 *
 * parseRule()
 */
export function mapQbJsonTreeToSearchTree(
  qbTree: JsonTree,
  rulePropsToSearchFilter: RulePropsToSearchFilterMapper,
  options: MapQbRulePropertiesToSearchFilterOptions
): SearchTreeNode {
  return processQbItem(qbTree, rulePropsToSearchFilter, options);
}

function processQbItem(
  item: JsonBasicItem,
  rulePropsToSearchFilter: RulePropsToSearchFilterMapper,
  options: MapQbRulePropertiesToSearchFilterOptions
) {
  if (item.type === 'group') {
    return processQbGroup(item as JsonGroup, rulePropsToSearchFilter, options);
  } else if (item.type === 'rule') {
    return processQbRule(item as JsonRule, rulePropsToSearchFilter, options);
  } else {
    throw new Error('We can only handle groups and rules at the moment');
  }
}

function processQbGroup(
  group: JsonGroup,
  rulePropsToSearchFilter: RulePropsToSearchFilterMapper,
  options: MapQbRulePropertiesToSearchFilterOptions
): SearchTreeInternalNode {
  const children = group.children1;
  if (!children) throw new Error('Missing children from group');
  if (!Array.isArray(children)) throw new Error('Expected children1 to be an array');

  // The recursive part
  const subNodes: SearchTreeNode[] = [];
  for (const child of children) {
    subNodes.push(processQbItem(child, rulePropsToSearchFilter, options));
  }

  // Query builder treats a missing oonjunction as having a default
  const op = mapConjunctionToMap(group?.properties?.conjunction ?? 'AND');

  // In our SearchTree 'not' is a set operation with a single node
  return group?.properties?.not
    ? { op: 'not', nodes: [{ op, nodes: subNodes }], _internalNodeId: group.id }
    : { op, nodes: subNodes, _internalNodeId: group.id };
}

function mapConjunctionToMap(conjunction: string) {
  if (conjunction === 'AND') return 'intersection';
  if (conjunction === 'OR') return 'union';
  throw new Error(`Unknown conjunction: ${conjunction}`);
}

function processQbRule(
  rule: JsonRule,
  rulePropsToSearchFilter: RulePropsToSearchFilterMapper,
  options: MapQbRulePropertiesToSearchFilterOptions
): SearchTreeLeafNode {
  const ruleId = rule.id;

  if (!ruleId) throw new Error('Missing id from rule. Excepted every RAQB rule to have an id set');

  const ruleIdType = typeof ruleId;
  if (ruleIdType !== 'string') {
    throw new Error(`Id of rule has unexpected type (${ruleIdType}). Excepected string.`);
  }

  return {
    op: 'filter',
    filter: rulePropsToSearchFilter(rule.properties, options),
    _leafNodeId: ruleId,
  };
}
